import { AlertTriangle, Info } from 'lucide-react';
import {
  AGB_URL,
  DISCLAIMER_TEXTS,
  getPoisonHotline,
} from '../../constants/disclaimers';

/**
 * DataDisclaimerBadge — Generischer Disclaimer für Pflanzendaten-Risikostellen.
 *
 * Drei Variants entsprechend HAFTUNG_PFLANZENDATEN.md § 2:
 *   - toxicity:  Amber, AlertTriangle, role=alert. Mit Giftnotruf tel:-Link.
 *                Sichtbar im PlantDetailDialog nur bei isToxic === true.
 *   - care:      Slate, Info, role=note. Footer der PflegekalenderView.
 *   - hardiness: Slate, Info, role=note. Standalone-Variante; in der
 *                HardinessWarningBadge-Integration wird stattdessen direkt
 *                der sourceText aus disclaimers.js in den Tooltip übernommen.
 *
 * Issue: #286
 *
 * @param {Object} props
 * @param {'toxicity'|'care'|'hardiness'} props.variant - required
 * @param {string|null} [props.country=null] - Country name from GardenSettingsContext (e.g. 'Österreich'). Falls back to DE.
 * @param {boolean} [props.compact=false] - Nur Icon + Tooltip, kein Volltext
 * @param {string} [props.className=''] - Tailwind-Override
 */
const DataDisclaimerBadge = ({ variant, country = null, compact = false, className = '' }) => {
  const hotline = getPoisonHotline(country);
  const config = DISCLAIMER_TEXTS[variant];
  if (!config) return null;

  const isToxicity = variant === 'toxicity';
  const Icon = isToxicity ? AlertTriangle : Info;

  const styles = isToxicity
    ? {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        icon: 'text-amber-700',
        text: 'text-amber-900',
        link: 'text-amber-900 underline underline-offset-2 hover:text-amber-950',
      }
    : {
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        icon: 'text-slate-600',
        text: 'text-slate-700',
        link: 'text-slate-800 underline underline-offset-2 hover:text-slate-950',
      };

  const agbLink = (
    <a
      href={AGB_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.link}
    >
      AGB
    </a>
  );

  if (compact) {
    return (
      <span
        role={config.role}
        aria-label={config.ariaLabel}
        title={config.sourceText}
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${styles.bg} border ${styles.border} ${className}`}
      >
        <Icon className={`w-3.5 h-3.5 ${styles.icon}`} aria-hidden="true" />
      </span>
    );
  }

  let body;
  if (variant === 'toxicity') {
    body = (
      <>
        Giftigkeitsangaben sind Orientierung. Bei Verdacht auf Vergiftung den{' '}
        <strong>Giftnotruf</strong> anrufen — in {hotline.region}{' '}
        <a
          href={`tel:${hotline.tel}`}
          className={`${styles.link} inline-flex items-center min-h-[44px] px-1 font-medium`}
        >
          {hotline.label}
        </a>{' '}
        — oder direkt zum Tierarzt. Verbindliche Bedingungen: {agbLink}.
      </>
    );
  } else if (variant === 'care') {
    body = (
      <>
        Pflegeempfehlungen sind Orientierung — keine Erfolgszusage. Verbindliche
        Bedingungen: {agbLink}.
      </>
    );
  } else {
    body = (
      <>
        Zonen-Angaben sind 30-Jahres-Mittelwerte (1991–2020), kein Wettergarant.
      </>
    );
  }

  return (
    <div
      role={config.role}
      aria-label={config.ariaLabel}
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${styles.bg} ${styles.border} ${className}`}
    >
      <Icon
        className={`w-4 h-4 flex-shrink-0 mt-0.5 ${styles.icon}`}
        aria-hidden="true"
      />
      <p className={`text-xs leading-relaxed ${styles.text}`}>{body}</p>
    </div>
  );
};

export default DataDisclaimerBadge;
